import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPicker } from '../components/MapPicker';
import { MapLocation, MapMarker } from '../types/maps';
import { api } from '../services/api';
import { IllegalParkingReport, ReportStatus } from "../types/illegalParkingReport";
import './CommunityPage.css';
import { DEFAULT_LOCATION } from '../utils/geocoding';

const CommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setViolations] = useState<IllegalParkingReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedViolation, setSelectedViolation] = useState<IllegalParkingReport | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [violationToConfirm, setViolationToConfirm] = useState<IllegalParkingReport | null>(null);
  const [mapCenter] = useState<MapLocation>(DEFAULT_LOCATION);
  const [currentBounds, setCurrentBounds] = useState<{ south: number; north: number; west: number; east: number }>({
    south: 40.751516, // Initial map bounds when DEFAULT_LOCATION is Times Square
    north: 40.764454,
    west: -74.000688,
    east: -73.970346
  });

  // Convert violations to map markers
  const mapMarkers: MapMarker[] = reports.map(report => ({
    id: report.reportId,
    lat: report.latitude,
    lng: report.longitude,
    type: report.status === ReportStatus.CONFIRMED ? ReportStatus.CONFIRMED : ReportStatus.SUBMITTED,
    onClick: () => setSelectedViolation(report)
  }));

  // Filter violations by status
  const confirmedViolations = reports.filter(v => ReportStatus.CONFIRMED === v.status);
  const pendingViolations = reports.filter(v => ReportStatus.SUBMITTED === v.status);

  const fetchViolations = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      }
      setError(null);

      const illegalParkingReports = await api.getNearbyReports(currentBounds);
      setViolations(illegalParkingReports);
    } catch (err) {
      setError('Failed to load violations');
      console.error('Error fetching violations:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (loading) {
      fetchViolations(false);
    }
  }, []);

  const handleRefresh = () => {
    fetchViolations(true);
  };

  // Handle map bounds changes with debouncing
  const handleBoundsChange = async(bounds: { south: number; north: number; west: number; east: number }) => {
    // Update current bounds
    setCurrentBounds(bounds);
  };

  const handleViolationConfirm = (violation: IllegalParkingReport) => {
    setViolationToConfirm(violation);
    setShowConfirmModal(true);
  };

  const handleConfirmProceed = () => {
    if (violationToConfirm) {
      // Navigate to confirmation page with violation data
      navigate('/confirm-violation', { 
        state: { 
          violation: violationToConfirm,
          isConfirmation: true
        } 
      });
    }
    setShowConfirmModal(false);
    setViolationToConfirm(null);
  };

  const handleConfirmCancel = () => {
    setShowConfirmModal(false);
    setViolationToConfirm(null);
  };

  if (loading) {
    return (
      <div className="community-page">
        <div className="community-header">
          <h1>My Community</h1>
          <p>Loading violations...</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="community-page">
        <div className="community-header">
          <h1>My Community</h1>
          <p className="error-message">{error}</p>
          <button className="refresh-button" onClick={handleRefresh}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="community-page">
      <div className="community-header">
        <div className="header-content">
          <div className="header-text">
            <h1>My Community</h1>
            <p>See all parking violations in your area</p>
          </div>
          <div className="header-actions">
            <button 
              className="submit-report-btn"
              onClick={() => navigate('/')}
              title="Submit a new parking violation report"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Submit New Report
            </button>
          </div>
        </div>
        
        <div className="violation-summary">
          <div className="summary-item">
            <span className="summary-count">{reports.length}</span>
            <span className="summary-label">Total Violations</span>
          </div>
          <div className="summary-item">
            <span className="summary-count confirmed">{confirmedViolations.length}</span>
            <span className="summary-label">Confirmed</span>
          </div>
          <div className="summary-item">
            <span className="summary-count pending">{pendingViolations.length}</span>
            <span className="summary-label">Pending</span>
          </div>
        </div>
      </div>

      <div className="community-content">
        <div className="map-section">
          <div className="map-container">
            <button 
              className="see-violations-btn" 
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh violations"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
              {refreshing ? 'Refreshing...' : 'See violations in this area'}
            </button>
            <MapPicker
              initialLocation={mapCenter}
              markers={mapMarkers}
              readOnly={true}
              height="400px"
              className="community-map"
              onBoundsChange={handleBoundsChange}
            />
          </div>
          
          <div className="map-legend">
            <div className="legend-item">
              <div className="legend-marker confirmed"></div>
              <span>Confirmed Violations ({confirmedViolations.length})</span>
            </div>
            <div className="legend-item">
              <div className="legend-marker pending"></div>
              <span>Pending Violations ({pendingViolations.length})</span>
            </div>
          </div>
        </div>

        {selectedViolation && (
          <div className="violation-details">
            <div className="violation-header">
              <h3>Violation Details</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedViolation(null)}
                title="Close details"
              >
                ×
              </button>
            </div>
            
            <div className="violation-info">
              <div className="info-row">
                <span className="label">License Plate:</span>
                <span className="value">{selectedViolation.licensePlate}</span>
              </div>
              <div className="info-row">
                <span className="label">State:</span>
                <span className="value">{selectedViolation.state}</span>
              </div>
              <div className="info-row">
                <span className="label">Violation Type:</span>
                <span className="value">{selectedViolation.reason}</span>
              </div>
              <div className="info-row">
                <span className="label">Location:</span>
                <span className="value">{selectedViolation.address}</span>
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`status ${selectedViolation.status}`}>
                  {selectedViolation.status}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Reported:</span>
                <span className="value">{selectedViolation.timestamp}</span>
              </div>
            </div>

            <div className="violation-photos">
              <h4>Photo Evidence ({selectedViolation.presignedUrlsForImages?.length || 0} photos)</h4>
              <div className="photo-grid">
                {selectedViolation.presignedUrlsForImages?.map((imageUrl, index) => (
                  <div key={index} className="photo-item">
                    <img src={imageUrl} alt={`Violation evidence ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>

            {selectedViolation.status === ReportStatus.SUBMITTED && (
              <button 
                className="confirm-btn"
                onClick={() => handleViolationConfirm(selectedViolation)}
              >
                Confirm Violation
              </button>
            )}
          </div>
        )}

        {!selectedViolation && (
          <div className="no-selection">
            <div className="no-selection-content">
              <svg viewBox="0 0 24 24" fill="currentColor" className="no-selection-icon">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <h3>Select a violation</h3>
              <p>Click on any marker on the map to view violation details</p>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && violationToConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <div className="modal-header">
              <h3>Confirm Violation</h3>
              <button 
                className="close-btn"
                onClick={handleConfirmCancel}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="confirmation-info">
                <div className="vehicle-info">
                  <h4>{violationToConfirm.licensePlate} ({violationToConfirm.state})</h4>
                  <p>{violationToConfirm.reason} • {violationToConfirm.address}</p>
                </div>
                
                <div className="confirmation-steps">
                  <h4>What happens next?</h4>
                  <ol>
                    <li>You'll be taken to a photo upload page</li>
                    <li>Take a new photo to confirm the vehicle is still there</li>
                    <li>This provides evidence of duration for the violation</li>
                    <li>The violation will be marked as confirmed</li>
                  </ol>
                </div>
                
                <div className="confirmation-note">
                  <p><strong>Note:</strong> This helps establish that the vehicle has been parked illegally for an extended period, strengthening the case for enforcement.</p>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="modal-btn secondary"
                onClick={handleConfirmCancel}
              >
                Cancel
              </button>
              <button 
                className="modal-btn primary"
                onClick={handleConfirmProceed}
              >
                Continue to Photo Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage; 